// src/pages/WeeklyExpensesPage.js
// Week-by-week tracking of actual expenses. This is intentionally separate
// from the Weekly Planner:
//   • Planner        = planning / distribution of expense amounts across weeks
//   • Weekly Expenses = recording/reviewing real expenses grouped by due date
// It edits the SAME Monthly/Annual expense data used everywhere else (no new
// data model), so amount edits show up on the Monthly/Annual tabs and in the
// Planner's "Monthly Actual" column, and status changes sync both ways with
// the Planner via the shared plannerState.
import React, { useState } from 'react';
import { useBudget } from '../context/BudgetContext';
import { EXPENSE_CATEGORIES, ALL_WEEKS } from '../utils/constants';
import MoneyInput from '../components/ui/MoneyInput';
import ExpenseSearch from '../components/ui/ExpenseSearch';
import { formatCurrencyValueForInput } from '../utils/currencyInput';
import { getPlannerKey } from '../utils/plannerKeys';

// Build a { categoryKey -> display name } lookup from the shared constants so
// category labels match the rest of the app.
const CATEGORY_NAME_LOOKUP = (() => {
  const map = {};
  [...EXPENSE_CATEGORIES.MONTHLY, ...EXPENSE_CATEGORIES.ANNUAL].forEach(cat => {
    map[cat.key] = cat.name;
  });
  return map;
})();

const getCategoryName = (categoryKey) =>
  CATEGORY_NAME_LOOKUP[categoryKey] ||
  categoryKey.charAt(0).toUpperCase() + categoryKey.slice(1);

// Which week (0-4) a plain "YYYY-MM-DD" date falls in, based on day-of-month.
// Parsed manually to avoid timezone shifts. Returns null when there's no date.
const getWeekIndexFromDate = (dateStr) => {
  if (!dateStr) return null;
  const day = parseInt(String(dateStr).split('-')[2], 10);
  if (!Number.isFinite(day)) return null;
  if (day <= 7) return 0;
  if (day <= 14) return 1;
  if (day <= 21) return 2;
  if (day <= 28) return 3;
  return 4;
};

const WeeklyExpensesPage = () => {
  const { state, actions, formatCurrency } = useBudget();
  const [currentWeek, setCurrentWeek] = useState(ALL_WEEKS);

  const accounts = Array.isArray(state.data.accounts) ? state.data.accounts : [];
  const accountName = (accountId) => {
    if (!accountId) return '—';
    const acc = accounts.find(a => a.id === accountId);
    return acc ? acc.name : '—';
  };

  // Flatten Monthly + Annual expenses into a single tracking list. Each row
  // remembers where it came from (categoryKey + index + isAnnual) so edits can
  // be written back to the original expense.
  const buildRows = () => {
    const rows = [];
    const push = (expense, categoryKey, index, isAnnual) => {
      if (!expense?.name || !expense.name.trim()) return;
      rows.push({
        rowKey: expense.id || `${categoryKey}-${index}`,
        expenseId: expense.id,
        name: expense.name,
        date: expense.date || '',
        categoryKey,
        index,
        categoryName: getCategoryName(categoryKey),
        accountId: expense.accountId || '',
        projected: parseFloat(expense.projected) || 0,
        // Fall back to the legacy `amount` field (like the rest of the app's
        // `actual || amount` handling) so older/imported amount-only expenses
        // aren't reported as $0.00 here.
        actual: parseFloat(expense.actual || expense.amount) || 0,
        paid: !!expense.paid,
        transferred: !!expense.transferred,
        isAnnual,
        weekIndex: getWeekIndexFromDate(expense.date)
      });
    };

    Object.entries(state.data.monthly || {}).forEach(([key, list]) =>
      (list || []).forEach((exp, i) => push(exp, key, i, false))
    );
    Object.entries(state.data.annual || {}).forEach(([key, list]) =>
      (list || []).forEach((exp, i) => push(exp, key, i, true))
    );
    return rows;
  };

  // Write an edited amount back to the original Monthly/Annual expense. The
  // shared reducer handles persistence, and the Planner's "Monthly Actual"
  // column reads the same field, so it updates automatically.
  const handleAmountChange = (row, field, value) => {
    const source = row.isAnnual ? state.data.annual : state.data.monthly;
    const list = source?.[row.categoryKey] || [];
    const expense = list[row.index] || {};
    const newValue = value === '' ? 0 : value;
    const updated = {
      ...expense,
      [field]: newValue,
      id: expense.id || `${row.categoryKey}-${Date.now()}-${row.index}`
    };
    if (row.isAnnual) {
      actions.updateAnnualExpense(row.categoryKey, updated, row.index);
    } else {
      actions.updateMonthlyExpense(row.categoryKey, updated, row.index);
    }

    // Push the new Actual into the Planner's week amounts too, using the same
    // rules as the source tabs so editing here or there behaves identically:
    //   • Monthly page: distributes when the expense's Auto-Distribute flag is on.
    //   • Annual page: always distributes the monthly equivalent (annual ÷ 12).
    // distributeMonthlyToWeekly places the amount in the due-date week.
    if (field === 'actual' && updated.name) {
      if (row.isAnnual) {
        const monthlyEquivalent = (parseFloat(newValue) || 0) / 12;
        setTimeout(() => {
          actions.distributeMonthlyToWeekly(updated, monthlyEquivalent, updated.date);
        }, 100);
      } else if (updated.autoDistributeToPlanner === true) {
        setTimeout(() => {
          actions.distributeMonthlyToWeekly(updated, parseFloat(newValue) || 0, updated.date);
        }, 100);
      }
    }
  };

  // Toggle paid/transferred for this row's due week. updateExpenseStatus is the
  // same cross-page sync used by the Monthly/Annual/Planner pages: it updates
  // the expense's own paid/transferred flag AND the plannerState week statuses,
  // so the Planner checkboxes reflect the change immediately (and vice versa).
  const handleStatusChange = (row, statusType, checked) => {
    actions.updateExpenseStatus(
      row.expenseId,
      row.name,
      row.weekIndex, // null (no date) = all weeks, matching existing semantics
      statusType,
      checked,
      'weekly-expenses'
    );
  };

  // Current status for a row: prefer the shared plannerState (what the Planner
  // shows for this row's week), falling back to the expense's own flag for
  // expenses that have no planner entry yet.
  const getRowStatus = (row, statusType) => {
    const key = getPlannerKey({ id: row.expenseId, name: row.name });
    const entry = state.data.plannerState?.[key] || state.data.plannerState?.[row.name];
    const statuses = entry?.[statusType];
    if (Array.isArray(statuses)) {
      return row.weekIndex === null
        ? statuses.some(Boolean)
        : !!statuses[row.weekIndex];
    }
    return statusType === 'paid' ? row.paid : row.transferred;
  };

  const allRows = buildRows();

  // Bucket rows by week (0-4) plus a "no date" bucket.
  const weekBuckets = [[], [], [], [], []];
  const noDateRows = [];
  allRows.forEach(row => {
    if (row.weekIndex === null) noDateRows.push(row);
    else weekBuckets[row.weekIndex].push(row);
  });

  const sumField = (rows, field) => rows.reduce((sum, r) => sum + (r[field] || 0), 0);

  // Which week sections to render given the selector.
  const visibleWeekIndexes =
    currentWeek === ALL_WEEKS ? [0, 1, 2, 3, 4] : [currentWeek - 1];

  // Monthly totals cover every dated week (no-date rows are shown separately
  // but still counted so the monthly figure reflects all tracked expenses).
  const monthlyProjected = sumField(allRows, 'projected');
  const monthlyActual = sumField(allRows, 'actual');

  // Searchable list for the shared ExpenseSearch box. Keys match each row's
  // data-expense-id so the search can scroll straight to the row.
  const searchItems = allRows.map(row => ({
    key: row.expenseId || row.rowKey,
    label: row.name,
    sublabel: `${row.categoryName} • ${row.weekIndex === null ? 'No due date' : `Week ${row.weekIndex + 1}`}`,
    weekIndex: row.weekIndex
  }));

  // If the searched expense lives outside the currently selected week,
  // switch back to All Weeks so its row exists on the page to scroll to.
  const revealForSearch = (item) => {
    if (currentWeek !== ALL_WEEKS && item.weekIndex !== currentWeek - 1) {
      setCurrentWeek(ALL_WEEKS);
    }
  };

  const renderRows = (rows) =>
    rows.map(row => (
      <tr key={row.rowKey} data-expense-id={row.expenseId || row.rowKey}>
        <td className="we-date">{row.date || '—'}</td>
        <td className="we-name">
          {row.name}
          {row.isAnnual && <span className="we-tag">Annual</span>}
        </td>
        <td>{row.categoryName}</td>
        <td>{accountName(row.accountId)}</td>
        <td className="we-amount">
          <MoneyInput
            className={`we-amount-input ${row.projected > 0 ? 'has-value' : ''}`}
            value={row.projected}
            placeholder="0.00"
            commitEmptyAs={0}
            formatDisplay={formatCurrencyValueForInput}
            onCommit={(val) => handleAmountChange(row, 'projected', val)}
          />
        </td>
        <td className="we-amount">
          <MoneyInput
            className={`we-amount-input ${row.actual > 0 ? 'has-value' : ''}`}
            value={row.actual}
            placeholder="0.00"
            commitEmptyAs={0}
            formatDisplay={formatCurrencyValueForInput}
            onCommit={(val) => handleAmountChange(row, 'actual', val)}
          />
        </td>
        <td className="we-status">
          <label className="we-check" title="Transferred (syncs with the Planner)">
            <input
              type="checkbox"
              className="transferred-checkbox"
              checked={getRowStatus(row, 'transferred')}
              onChange={(e) => handleStatusChange(row, 'transferred', e.target.checked)}
            />
            <span>T</span>
          </label>
          <label className="we-check" title="Paid (syncs with the Planner)">
            <input
              type="checkbox"
              className="paid-checkbox"
              checked={getRowStatus(row, 'paid')}
              onChange={(e) => handleStatusChange(row, 'paid', e.target.checked)}
            />
            <span>P</span>
          </label>
        </td>
      </tr>
    ));

  const renderSection = (title, rows) => (
    <div className="we-section" key={title}>
      <div className="we-section-header">
        <span className="we-section-title">{title}</span>
        <span className="we-section-totals">
          Projected: <strong>{formatCurrency(sumField(rows, 'projected'))}</strong>
          {'  •  '}
          Actual: <strong>{formatCurrency(sumField(rows, 'actual'))}</strong>
          {'  •  '}
          {rows.length} item{rows.length === 1 ? '' : 's'}
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="we-empty">No expenses scheduled for this week.</div>
      ) : (
        <div className="we-table-wrap">
          <table className="we-table">
            <thead>
              <tr>
                <th style={{ width: '120px' }}>Date</th>
                <th>Expense</th>
                <th>Category</th>
                <th>Account</th>
                <th style={{ width: '120px' }}>Projected</th>
                <th style={{ width: '120px' }}>Actual</th>
                <th style={{ width: '96px' }}>Status</th>
              </tr>
            </thead>
            <tbody>{renderRows(rows)}</tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="main-content weekly-expenses-page no-top-gap">
      <style>{`
        .main-content.no-top-gap { margin-top: 0 !important; padding-top: 8px !important; }
        .weekly-expenses-page { background-color: var(--bg-primary); min-height: 100vh; padding: 20px 0; }
        .weekly-expenses-page .page-title { color: var(--text-primary); margin: 0 0 8px; font-size: 1.8rem; font-weight: 600; }
        .weekly-expenses-page .page-description { color: var(--text-secondary); margin-bottom: 20px; }

        .we-note {
          background: linear-gradient(135deg, rgba(33,150,243,0.1), rgba(33,150,243,0.05));
          border: 1px solid rgba(33,150,243,0.3);
          border-radius: 8px; padding: 14px 16px; margin-bottom: 20px;
          font-size: 0.92rem; color: var(--text-primary);
        }

        .we-controls {
          display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
          margin-bottom: 20px; padding: 15px;
          background: var(--card-bg); border: 1px solid var(--card-border);
          border-radius: 8px; box-shadow: var(--card-shadow);
        }
        .we-controls label { font-weight: 600; color: var(--text-primary); }
        .we-select {
          padding: 8px 12px; border: 1px solid var(--input-border); border-radius: 6px;
          background: var(--input-bg); color: var(--text-primary); cursor: pointer; font-size: 0.9rem;
        }

        .we-section {
          margin-bottom: 22px; border: 1px solid var(--card-border); border-radius: 12px;
          overflow: hidden; box-shadow: var(--card-shadow); background: var(--card-bg);
        }
        .we-section-header {
          display: flex; justify-content: space-between; align-items: center; gap: 12px;
          flex-wrap: wrap; padding: 14px 18px; color: #fff;
          background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
        }
        .we-section-title { font-size: 1.1rem; font-weight: 700; }
        .we-section-totals { font-size: 0.85rem; opacity: 0.95; }

        .we-empty { padding: 16px 18px; color: var(--text-muted); font-style: italic; }

        .we-table-wrap { overflow-x: auto; }
        .we-table { width: 100%; border-collapse: collapse; min-width: 760px; font-size: 0.88rem; }
        .we-table th, .we-table td {
          padding: 10px 12px; border-bottom: 1px solid var(--border-light);
          text-align: left; color: var(--text-primary);
        }
        .we-table th {
          background: var(--bg-secondary); color: var(--text-secondary);
          font-weight: 600; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.03em;
        }
        .we-table tbody tr:hover { background: var(--hover-bg); }
        .we-amount { text-align: right !important; }
        .we-amount-input {
          width: 100px; padding: 6px 8px; border: 1px solid var(--input-border);
          border-radius: 4px; font-size: 0.85rem; text-align: right;
          background-color: var(--input-bg); color: var(--text-primary);
          box-sizing: border-box;
        }
        .we-amount-input:focus { outline: 2px solid var(--primary); outline-offset: -1px; }
        .we-amount-input.has-value {
          background: linear-gradient(135deg, #e8f5e8 0%, #f0f8f0 100%) !important;
          border-color: var(--success) !important;
          font-weight: 600 !important;
          color: #155724 !important;
        }
        .we-date { white-space: nowrap; color: var(--text-secondary); }
        .we-name { font-weight: 600; }
        .we-tag {
          display: inline-block; margin-left: 8px; padding: 1px 7px; font-size: 0.68rem;
          font-weight: 600; border-radius: 10px; background: rgba(255,193,7,0.18);
          color: #8a6d00; border: 1px solid rgba(255,193,7,0.5);
        }
        .we-status { white-space: nowrap; }
        .we-check {
          display: inline-flex; align-items: center; gap: 3px; margin: 0 5px 0 0;
          font-size: 0.8rem; font-weight: 700; cursor: pointer; color: var(--text-primary);
        }
        .we-check input { cursor: pointer; transform: scale(1.1); }
        .we-check .transferred-checkbox:checked { accent-color: #ffc107; }
        .we-check .paid-checkbox:checked { accent-color: var(--success); }

        .we-summary {
          margin-top: 26px; padding: 22px; border-radius: 12px; text-align: center;
          background: linear-gradient(135deg, var(--card-bg) 0%, var(--bg-secondary) 100%);
          border: 1px solid var(--card-border); box-shadow: var(--card-shadow);
        }
        .we-summary h3 { margin: 0 0 14px; color: var(--text-primary); font-size: 1.15rem; }
        .we-summary-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; max-width: 520px; margin: 0 auto; }
        .we-summary-label { font-size: 0.85rem; color: var(--text-secondary); }
        .we-summary-value { font-size: 1.4rem; font-weight: 700; color: var(--primary); }

        @media (max-width: 600px) {
          .we-summary-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <h2 className="page-title">🗓️ Weekly Expenses</h2>
      <p className="page-description">
        Record and review your actual expenses week by week, grouped by their due date.
      </p>

      <div className="we-note">
        💡 <strong>Weekly Expenses</strong> is for <strong>tracking</strong> the real bills due each week —
        edit the Projected/Actual amounts and tick <strong>T</strong>/<strong>P</strong> as money moves.
        Edits update the Monthly/Annual tabs, and statuses sync both ways with the <strong>Planner</strong>
        (which stays the place to <strong>plan and distribute</strong> amounts across weeks).
        Expenses are placed in a week by day of month (1–7 = Week 1, 8–14 = Week 2, and so on).
      </div>

      <div className="we-controls">
        <label htmlFor="we-week-select">Show:</label>
        <select
          id="we-week-select"
          className="we-select"
          value={currentWeek}
          onChange={(e) => setCurrentWeek(parseInt(e.target.value, 10))}
        >
          <option value={ALL_WEEKS}>All Weeks</option>
          <option value={1}>Week 1 (days 1–7)</option>
          <option value={2}>Week 2 (days 8–14)</option>
          <option value={3}>Week 3 (days 15–21)</option>
          <option value={4}>Week 4 (days 22–28)</option>
          <option value={5}>Week 5 (days 29+)</option>
        </select>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.88rem' }}>
          {allRows.length} tracked expense{allRows.length === 1 ? '' : 's'}
        </span>
        <ExpenseSearch items={searchItems} onBeforeNavigate={revealForSearch} />
      </div>

      {allRows.length === 0 ? (
        <div className="we-section">
          <div className="we-empty">
            No expenses found. Add expenses on the Monthly or Annual tabs to see them here.
          </div>
        </div>
      ) : (
        <>
          {visibleWeekIndexes.map(weekIdx =>
            renderSection(`Week ${weekIdx + 1}`, weekBuckets[weekIdx])
          )}

          {/* Undated expenses can't be placed in a week; show them only in the
              All Weeks view so nothing is silently hidden. */}
          {currentWeek === ALL_WEEKS && noDateRows.length > 0 &&
            renderSection('No Due Date', noDateRows)}

          <div className="we-summary">
            <h3>📋 Monthly Totals</h3>
            <div className="we-summary-grid">
              <div>
                <div className="we-summary-label">Total Projected</div>
                <div className="we-summary-value">{formatCurrency(monthlyProjected)}</div>
              </div>
              <div>
                <div className="we-summary-label">Total Actual</div>
                <div className="we-summary-value">{formatCurrency(monthlyActual)}</div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WeeklyExpensesPage;
